import React, { useEffect, useState } from 'react'
import { Platform, TextStyle, View, ViewStyle } from 'react-native'
import { IMSTArray, Instance } from 'mobx-state-tree'
import { UserCertificationCriteria, TopicTest } from '@trivie/core/src/models/UserTopicDetails'
import { AnimatedGradientProgress } from '../animated-gradient-progress'
import { color } from '../../theme'
import { Text } from '../text'
import { Medallion } from '../medallion'
import { CertificationTooltip } from '../certification-tooltip/certification-tooltip'
import { load, save } from '@trivie/core'

interface TopicProgressProps {
  visible: boolean
  preTest?: Instance<typeof TopicTest> | undefined | null
  postTest?: Instance<typeof TopicTest> | undefined | null
  quizCount: number
  completedQuizzes: number
  leftIcon?: React.ReactElement | null
  rightIcon?: React.ReactElement | null
  rightIconStyle?: ViewStyle
  leftIconStyle?: ViewStyle
  scoreContainerStyle?: ViewStyle
  progressbarContainerStyle?: ViewStyle
  progressbarStyle?: any
  header?: React.ReactElement | null
  rightIconPress?: ({ nativeEvent }: any) => void
  certification?:
    | {
        info: IMSTArray<typeof UserCertificationCriteria> | null
        certification_level: string | null
      }
    | null
    | undefined
  topicID?: number | null | undefined
  style?: ViewStyle
}

export const TopicProgress = (props: TopicProgressProps) => {
  const {
    visible,
    leftIcon,
    rightIconStyle,
    leftIconStyle,
    rightIcon,
    preTest,
    postTest,
    completedQuizzes,
    quizCount,
    progressbarContainerStyle,
    progressbarStyle,
    scoreContainerStyle,
    header,
    certification,
    topicID,
    style,
  } = props

  const [showTooltip, setShowTooltip] = useState(false)

  const renderHeader = React.useCallback(() => {
    if (!header) {
      return (
        <View style={{ flexDirection: 'row', marginBottom: 14 }}>
          <Text
            style={{ color: color.shade50 }}
            variant="body1Emphasized"
            tx="Scoreboard.topicCompletion"
          />
          <Text
            style={{ color: color.shade70 }}
            variant="body1Emphasized"
            tx="Topic.completionText"
            txOptions={{ count: completedQuizzes, total: quizCount }}
          />
        </View>
      )
    }
    return header
  }, [header])
  const anchorRef = React.useRef<any>(null)

  useEffect(() => {
    ;(async () => {
      if (certification) {
        const show = (await load('cert-shown')) ? false : true
        setShowTooltip(show)
      }
    })()
  }, [certification])

  const onClose = (event: any) => {
    if (Platform.OS === 'web') {
      if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
        return
      }
    }
    setShowTooltip(false)
    save('cert-shown', true)
  }

  return visible ? (
    <View style={{ ...ROOT, ...style }}>
      {renderHeader()}
      <AnimatedGradientProgress
        style={{
          marginBottom: 12,
          alignSelf: 'center',
          width: '100%',
          ...progressbarContainerStyle,
        }}
        progressbarStyle={progressbarStyle}
        leftIcon={leftIcon}
        rightIconStyle={rightIconStyle}
        rightPress={() => setShowTooltip(true)}
        rightIcon={
          certification ? (
            <View style={{ position: 'absolute', top: -36, right: -36 }} ref={anchorRef}>
              <Medallion
                scale={0.42}
                type={
                  completedQuizzes !== quizCount ? 'Started' : certification.certification_level
                }
              />
            </View>
          ) : postTest && postTest.completed ? (
            rightIcon
          ) : null
        }
        leftIconStyle={leftIconStyle}
        value={completedQuizzes / quizCount}
      />
      <View style={{ ...SCORE_CONTAINER, ...scoreContainerStyle }}>
        {preTest && (
          <View style={{ flexDirection: 'column' }}>
            <Text style={LABEL} variant="body1" tx="Quiz.preTest" />
            <Text style={TEST_SCORE} variant="body1Emphasized" text={`${preTest.score}%`} />
          </View>
        )}
        {postTest && (
          <View style={{ flexDirection: 'column' }}>
            <Text style={LABEL} variant="body1" tx="Quiz.postTest" />
            {postTest.completed && (
              <Text style={POST_TEST_SCORE} variant="body1Emphasized" text={`${postTest.score}%`} />
            )}
          </View>
        )}
      </View>
      <CertificationTooltip
        criteria={certification?.info}
        anchorRef={anchorRef}
        handleClose={onClose}
        visible={showTooltip}
      />
    </View>
  ) : null
}

const ROOT: ViewStyle = {
  zIndex: 999,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
}
const SCORE_CONTAINER: ViewStyle = {
  marginBottom: 20,
  width: '100%',
  flexDirection: 'row',
  justifyContent: 'space-between',
}
const LABEL: TextStyle = { fontSize: 12, color: color.shade70 }
const TEST_SCORE: TextStyle = { fontWeight: '700', fontSize: 12, color: color.shade70 }
const POST_TEST_SCORE: TextStyle = { ...TEST_SCORE, textAlign: 'right' }
